/**
 * identityBuilder — sub-container do slice de identidade/acessos.
 *
 * Agrupa gestão de Usuários (CRUD + bloqueio), Roles (read-only) e
 * Convites por e-mail. O AuthService (login/refresh) continua no fluxo de auth.
 *
 * @since sprint Identity (FE espelhando identity-api do BE)
 */
import type { $Fetch } from 'ofetch';

import { HttpUserGateway } from '@/infrastructure/http/HttpUserGateway';
import { HttpRoleGateway } from '@/infrastructure/http/HttpRoleGateway';
import { HttpInvitationGateway } from '@/infrastructure/http/HttpInvitationGateway';
import { HttpMeGateway } from '@/infrastructure/http/HttpMeGateway';
import { HttpLgpdGateway } from '@/infrastructure/http/HttpLgpdGateway';

import {
  ListUsers,
  CreateUser,
  UpdateUser,
  DeactivateUser,
  BlockUser,
  UnblockUser,
} from '@/application/usecase/UserUseCaseImpl';
import {
  ListRoles,
  ListPermissions,
  CreateRole,
  UpdateRole,
  SetRolePermissions,
  DeactivateRole,
} from '@/application/usecase/RoleUseCaseImpl';
import {
  ListInvitations,
  CreateInvitation,
  ResendInvitation,
  RevokeInvitation,
  AcceptInvitation,
} from '@/application/usecase/InvitationUseCaseImpl';
import {
  GetMe,
  UpdateMe,
  ChangePassword,
  SetupTotp,
  EnableTotp,
  DisableTotp,
} from '@/application/usecase/MeUseCaseImpl';
import {
  ExportarDadosLgpd,
  SolicitarExclusaoLgpd,
  ListConsentimentos,
  RegistrarConsentimento,
} from '@/application/usecase/LgpdUseCaseImpl';

export function buildIdentitySlice(httpClient: $Fetch, baseURL: string) {
  const userGateway = new HttpUserGateway(httpClient);
  const roleGateway = new HttpRoleGateway(httpClient);
  const invitationGateway = new HttpInvitationGateway(httpClient, baseURL);
  const meGateway = new HttpMeGateway(httpClient);
  const lgpdGateway = new HttpLgpdGateway(httpClient);

  const useCases = {
    listUsers: new ListUsers(userGateway),
    createUser: new CreateUser(userGateway),
    updateUser: new UpdateUser(userGateway),
    deactivateUser: new DeactivateUser(userGateway),
    blockUser: new BlockUser(userGateway),
    unblockUser: new UnblockUser(userGateway),
    listRoles: new ListRoles(roleGateway),
    listPermissions: new ListPermissions(roleGateway),
    createRole: new CreateRole(roleGateway),
    updateRole: new UpdateRole(roleGateway),
    setRolePermissions: new SetRolePermissions(roleGateway),
    deactivateRole: new DeactivateRole(roleGateway),
    acceptInvitation: new AcceptInvitation(invitationGateway),
    listInvitations: new ListInvitations(invitationGateway),
    createInvitation: new CreateInvitation(invitationGateway),
    resendInvitation: new ResendInvitation(invitationGateway),
    revokeInvitation: new RevokeInvitation(invitationGateway),
    getMe: new GetMe(meGateway),
    updateMe: new UpdateMe(meGateway),
    changePassword: new ChangePassword(meGateway),
    setupTotp: new SetupTotp(meGateway),
    enableTotp: new EnableTotp(meGateway),
    disableTotp: new DisableTotp(meGateway),
    exportarDadosLgpd: new ExportarDadosLgpd(lgpdGateway),
    solicitarExclusaoLgpd: new SolicitarExclusaoLgpd(lgpdGateway),
    listConsentimentos: new ListConsentimentos(lgpdGateway),
    registrarConsentimento: new RegistrarConsentimento(lgpdGateway),
  };

  return { useCases };
}
